import type { PrintOptions, PrintResult, Printer } from './printTypes';

/**
 * Implementación de impresión para navegador.
 *
 * El navegador no permite enumerar las impresoras del sistema por seguridad,
 * así que la impresión se delega al diálogo nativo del dispositivo.
 */
export class WebPrintService {
  readonly runtime = 'WEB' as const;

  async listPrinters(): Promise<Printer[]> {
    // El navegador no expone las impresoras del sistema. No se inventan.
    return [];
  }

  async printHTML(html: string, options?: PrintOptions): Promise<PrintResult> {
    this.printViaIframe(html, options);
    return { success: true, message: 'Diálogo de impresión del navegador abierto.' };
  }

  async printTest(_printerName?: string): Promise<PrintResult> {
    return this.printHTML('<!doctype html><html><body>Prueba</body></html>');
  }

  async openCashDrawer(): Promise<PrintResult> {
    return {
      success: false,
      message: 'La apertura de gaveta requiere la aplicación de escritorio (Electron).',
    };
  }

  private printViaIframe(html: string, options?: PrintOptions): void {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');

    // CLAVE DE NITIDEZ: el iframe se mantiene DENTRO del viewport (posición 0,0)
    // y se oculta solo con opacidad. Si se envía fuera de pantalla (p. ej.
    // left: -10000px) el navegador lo rasteriza a resolución de pantalla antes
    // de imprimir y el texto sale borroso. Con el nodo en su lugar, el motor de
    // impresión compone en texto vectorial nítido.
    const paperWidth =
      options?.paperWidth === 'A4' ? '210mm' : options?.paperWidth === '58mm' ? '58mm' : '80mm';
    iframe.style.position = 'fixed';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = paperWidth;
    iframe.style.height = '100vh';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    iframe.style.zIndex = '-1';
    document.body.appendChild(iframe);

    const contentWindow = iframe.contentWindow;
    const doc = contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 2000);
    };

    const doPrint = () => {
      try {
        contentWindow?.focus();
        contentWindow?.print();
      } catch {
        // Si el diálogo falla, no rompemos la aplicación.
      } finally {
        cleanup();
      }
    };

    // Espera a fuentes e imágenes para que la impresión salga completa y nítida.
    const waitForAssets = async (): Promise<void> => {
      const innerDoc = contentWindow?.document;
      if (!innerDoc) return;
      const images = Array.from(innerDoc.images || []);
      await Promise.all(
        images.map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
              }),
        ),
      );
      try {
        const fonts = (innerDoc as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
        if (fonts?.ready) await fonts.ready;
      } catch {
        // Ignorar: si no hay API de fuentes seguimos con la impresión.
      }
    };

    let printed = false;
    const trigger = () => {
      if (printed) return;
      printed = true;
      doPrint();
    };

    iframe.addEventListener('load', () => {
      waitForAssets().then(() => setTimeout(trigger, 150));
    });

    // Respaldo por si el evento load no se dispara a tiempo.
    setTimeout(() => {
      waitForAssets().then(() => setTimeout(trigger, 0));
    }, 1500);
  }
}